import { Reveal } from "@components/ui";

const initiatives = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: "HSE Training",
    description: "Comprehensive health, safety & environment training for all field teams with hands-on simulations and certifications.",
    color: "#122d63",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    title: "Sustainability Awareness",
    description: "Cross-departmental programs building environmental consciousness and embedding green practices into daily operations.",
    color: "#1a7a4f",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
    title: "Career Growth Paths",
    description: "Structured development pathways with mentoring, internal promotions, and leadership development programs.",
    color: "#f03813",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: "Diversity & Inclusion",
    description: "A multicultural workforce spanning 40+ nationalities, united by shared values and mutual respect.",
    color: "#6b4fc7",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
    title: "Employee Wellness",
    description: "Safety-first culture with wellness programs, mental health support, and work-life balance initiatives.",
    color: "#d4880f",
  },
];

const stats = [
  {
    value: "500+",
    label: "Trained Professionals",
    color: "#122d63",
    bgGradient: "from-[#122d63]/10 to-[#122d63]/5",
  },
  {
    value: "98%",
    label: "Safety Record",
    color: "#1a7a4f",
    bgGradient: "from-[#1a7a4f]/10 to-[#1a7a4f]/5",
  },
  {
    value: "40+",
    label: "Nationalities",
    color: "#d4880f",
    bgGradient: "from-[#d4880f]/10 to-[#d4880f]/5",
  },
  {
    value: "15K+",
    label: "Training Hours/Year",
    color: "#6b4fc7",
    bgGradient: "from-[#6b4fc7]/10 to-[#6b4fc7]/5",
  },
];

const WorkforceDevelopmentSection = () => {
  return (
    <section className="bg-[#f9f9fb] py-12 md:py-16 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="max-w-[1368px] mx-auto">
        {/* Header */}
        <Reveal>
          <div className="flex flex-col items-center text-center mb-10 md:mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/10 text-[#122d63] text-[14px] md:text-[16px] font-medium tracking-[-0.04em] mb-4">
              Our People
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63]">
              Investing in Our Workforce
            </h2>
            <p className="text-[20px] md:text-[24px] lg:text-[28px] text-[#62636c] tracking-[-0.04em] mt-3 max-w-[700px]">
              Developing talent that drives sustainable growth
            </p>
          </div>
        </Reveal>

        {/* Stats Banner */}
        <Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12 lg:mb-16">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className={`relative overflow-hidden rounded-[20px] bg-gradient-to-br ${stat.bgGradient} p-6 md:p-8 text-center group hover:shadow-lg transition-all duration-300`}
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: `radial-gradient(circle at 50% 120%, ${stat.color}15, transparent 70%)`,
                  }}
                />
                <span
                  className="relative z-10 block text-[36px] md:text-[48px] lg:text-[56px] font-bold tracking-[-0.04em] leading-none"
                  style={{ color: stat.color }}
                >
                  {stat.value}
                </span>
                <span className="relative z-10 block text-[14px] md:text-[16px] text-[#62636c] font-medium tracking-[-0.04em] mt-2">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Initiative Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {initiatives.map((item, index) => (
            <Reveal key={index} className={`h-full ${index >= 3 ? "lg:col-span-1" : ""}`}>
              <div
                className={`group relative overflow-hidden rounded-[24px] border border-[rgba(98,99,108,0.2)] bg-white p-6 md:p-8 hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-300 h-full ${
                  index >= 3 ? "" : ""
                }`}
              >
                {/* Decorative gradient blob */}
                <div
                  className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-[0.06] group-hover:opacity-[0.12] group-hover:scale-125 transition-all duration-500"
                  style={{ background: `radial-gradient(circle, ${item.color}, transparent)` }}
                />

                {/* Icon */}
                <div
                  className="relative z-10 w-14 h-14 rounded-[16px] flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: item.color }}
                >
                  {item.icon}
                </div>

                {/* Number watermark */}
                <span className="absolute top-3 right-4 text-[72px] font-bold text-[#f1f1f3] leading-none select-none pointer-events-none group-hover:text-[#e8e9ed] transition-colors duration-300">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <h3 className="relative z-10 text-[20px] md:text-[24px] font-semibold text-[#090d2c] tracking-[-0.04em] mb-2">
                  {item.title}
                </h3>
                <p className="relative z-10 text-[16px] md:text-[18px] text-[rgba(10,14,46,0.75)] tracking-[-0.04em] leading-relaxed">
                  {item.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Bottom row — last 2 cards centered */}
        {initiatives.length % 3 !== 0 && (
          <div className="hidden lg:flex justify-center mt-6 gap-6">
            {/* This handles the visual centering — the grid above already works correctly for all screen sizes */}
          </div>
        )}
      </div>
    </section>
  );
};

export default WorkforceDevelopmentSection;
